import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RING, fillFor } from '../wall/wallTokens';
import { T, RADIUS, BODY_FONT } from './messagesModel';
import {
  SECURE, PIN_LENGTH, PIN_KEYS, PAD_SIZE, PATTERN_MIN, PIN_SUBMIT_DELAY_MS, PATTERN_SUBMIT_DELAY_MS, ERROR_FLASH_MS,
  dotCentre, hitDot, padPoint, extendPattern, encodePattern, patternLines, sheetCopy, lockoutError, SETUP_NOTE, FORGOT_NOTE,
  LOCK_TYPE_LABEL,
} from './secureModel';

// A Lucide "lock" stand-in, drawn to the DS stroke (square caps, miter joins); the DS
// iconography rule asks for the substitution to be flagged. Shared by the sheet, the
// inbox header, the vault bar, the rows and the SECURE pill.
export function LockIcon({ size = 15, strokeWidth = 2.5, style, ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="square" strokeLinejoin="miter" aria-hidden style={style} {...rest}>
      <rect x="4" y="11" width="16" height="10" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

/**
 * The lock sheet (handoff §5): one black card over Messages for setting a lock,
 * unlocking, and the FORGOT reset. The sheet owns the input (digits, the drawn path,
 * the password) and the error flash; the page owns the lock itself and answers each
 * submit through the callbacks, which return `{ ok, error }`.
 *
 *   modal  { kind: 'setup' | 'unlock', step: 'enter' | 'confirm' | 'forgot', type: 'pin' | 'pattern',
 *            change: bool, lockedUntil: ms | null, triesLeft }
 *   onSetup(type, code)     setup step 2 matched → save (async; { ok, error })
 *   onUnlock(code)          unlock submit (async; { ok, error, lockedUntil, triesLeft })
 *   onForgot(password)      reset submit (async; { ok, error })
 *   onClose()
 *   biometric               { label, unlock, auto } when this device can open the lock with its face:
 *                           the sheet offers a USE {LABEL} button, and asks on open when `auto`
 *                           (a device that has answered before)
 */
export default function SecureLockSheet({ modal, onSetup, onUnlock, onForgot, onClose, biometric = null }) {
  const [type, setType] = useState(modal.type || 'pin');
  const [step, setStep] = useState(modal.step || 'enter');
  const [first, setFirst] = useState(null);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(modal.lockedUntil || null);
  const [now, setNow] = useState(Date.now());
  // The drawn path: { seq, px, py, drawing, error }
  const [pat, setPat] = useState(null);
  const padRef = useRef(null);
  const timers = useRef([]);
  const later = useCallback((fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t); }, []);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // The lockout countdown.
  useEffect(() => {
    if (!lockedUntil) return undefined;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [lockedUntil]);
  const lockedOut = !!lockedUntil && lockedUntil > now;
  useEffect(() => { if (lockedUntil && !lockedOut) setLockedUntil(null); }, [lockedUntil, lockedOut]);

  const forgot = step === 'forgot';
  const isPin = !forgot && type === 'pin';
  const isPattern = !forgot && type === 'pattern';
  const copy = sheetCopy({ kind: modal.kind, step, type, change: modal.change, confirm: modal.confirm });

  // The face first, on open. A dismissed prompt leaves the keypad; a failure says why.
  const bioRef = useRef(biometric);
  bioRef.current = biometric;
  const askFace = useCallback(async () => {
    const b = bioRef.current;
    if (!b || busy) return;
    setBusy(true);
    const r = await b.unlock();
    setBusy(false);
    if (r.ok || r.cancelled) return;
    if (r.lockedUntil) { setLockedUntil(r.lockedUntil); setError(''); return; }
    if (r.error) setError(r.error);
  }, [busy]);
  // Once per open. The flag is set when the prompt fires, not when the effect runs, so a
  // StrictMode mount → cleanup → mount doesn't swallow it.
  const askedRef = useRef(false);
  useEffect(() => {
    if (modal.kind !== 'unlock' || !biometric || biometric.auto === false) return undefined;
    const t = setTimeout(() => { if (askedRef.current) return; askedRef.current = true; askFace(); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flashError = useCallback((msg) => {
    setInput('');
    setError(msg);
    setPat((p) => (p ? { ...p, error: true, drawing: false } : null));
    later(() => setPat((p) => (p && p.error ? null : p)), ERROR_FLASH_MS);
  }, [later]);

  const submit = useCallback(async (value) => {
    if (busy) return;
    if (modal.kind === 'unlock') {
      setBusy(true);
      const r = await onUnlock(value);
      setBusy(false);
      if (r.ok) return;
      if (r.lockedUntil) { setLockedUntil(r.lockedUntil); setInput(''); setPat(null); setError(''); return; }
      flashError(r.error);
      return;
    }
    if (step === 'enter') { setFirst(value); setStep('confirm'); setInput(''); setPat(null); setError(''); return; }
    if (value !== first) { setStep('enter'); setFirst(null); flashError("DIDN'T MATCH · START AGAIN"); return; }
    setBusy(true);
    const r = await onSetup(type, value);
    setBusy(false);
    if (!r.ok) { setStep('enter'); setFirst(null); flashError(r.error || 'THAT DIDN’T WORK'); }
  }, [busy, modal.kind, step, first, type, onUnlock, onSetup, flashError]);

  // The digits typed so far, readable from the key handler without a stale closure.
  const inputRef = useRef('');
  inputRef.current = input;
  const pinPress = useCallback((k) => {
    if (!isPin || lockedOut || busy) return;
    const v = inputRef.current;
    if (k === 'DEL') { setInput(v.slice(0, -1)); return; }
    if (!k || v.length >= PIN_LENGTH) return;
    const next = v + k;
    inputRef.current = next;
    setInput(next);
    setError('');
    // The fourth dot is seen filled before the answer comes back.
    if (next.length === PIN_LENGTH) later(() => submit(next), PIN_SUBMIT_DELAY_MS);
  }, [isPin, lockedOut, busy, later, submit]);

  // A physical keyboard: digits enter, Backspace deletes, Esc closes.
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') { onClose(); return; }
      if (forgot) return;
      if (/^[0-9]$/.test(e.key)) pinPress(e.key);
      else if (e.key === 'Backspace') pinPress('DEL');
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [forgot, pinPress, onClose]);

  const pickType = (t) => { setType(t); setStep('enter'); setFirst(null); setInput(''); setPat(null); setError(''); };

  // ── The pattern pad ──
  const padPt = (e) => padPoint(e.clientX, e.clientY, padRef.current.getBoundingClientRect());
  const patDown = (e) => {
    e.preventDefault();
    if (lockedOut || busy) return;
    try { padRef.current.setPointerCapture(e.pointerId); } catch (err) { /* older WebKit */ }
    const p = padPt(e);
    const h = hitDot(p);
    setPat({ seq: h == null ? [] : [h], px: p.x, py: p.y, drawing: true, error: false });
    setError('');
  };
  const patMove = (e) => {
    setPat((cur) => {
      if (!cur || !cur.drawing) return cur;
      const p = padPt(e);
      return { ...cur, seq: extendPattern(cur.seq, hitDot(p)), px: p.x, py: p.y };
    });
  };
  const patUp = () => {
    const cur = pat;
    if (!cur || !cur.drawing) return;
    if (cur.seq.length === 0) { setPat(null); return; }
    if (cur.seq.length < PATTERN_MIN) { flashError(`CONNECT AT LEAST ${PATTERN_MIN} DOTS`); return; }
    setPat({ ...cur, drawing: false });
    later(() => submit(encodePattern(cur.seq)), PATTERN_SUBMIT_DELAY_MS);
  };

  const submitPw = async () => {
    if (busy) return;
    if (!pw.trim()) { setError('ENTER YOUR PASSWORD'); return; }
    setBusy(true);
    const r = await onForgot(pw);
    setBusy(false);
    if (r.ok) { setPw(''); setStep('enter'); setFirst(null); setInput(''); setPat(null); setError(''); }
    else setError((r.error || 'THAT DIDN’T WORK').toUpperCase());
  };

  const shownError = lockedOut && !forgot ? lockoutError(lockedUntil, now) : error;
  const padOpacity = lockedOut && modal.kind === 'unlock' ? 0.3 : 1;
  const seq = pat ? pat.seq : [];
  const patErr = !!(pat && pat.error);
  const lines = patternLines(seq, pat && pat.drawing ? { x: pat.px, y: pat.py } : null);
  const footLink = { background: 'none', border: 0, fontSize: 10, fontWeight: 700, letterSpacing: '.12em', color: T.white, padding: '6px 0', textDecoration: 'underline', fontFamily: 'inherit', cursor: 'pointer' };

  return (
    <div data-secure-sheet role="dialog" aria-modal="true" aria-label={copy.title} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'absolute', inset: 0, zIndex: 95, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div className="font-mono" style={{ position: 'relative', width: 400, maxWidth: '100%', background: `linear-gradient(#000,#000) padding-box, ${RING} border-box`, border: '2px solid transparent', color: T.white, borderRadius: RADIUS.modal, padding: '28px 32px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, overflow: 'hidden', boxSizing: 'border-box' }}>
        <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: SECURE.scanlines }} />

        <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: SECURE.muted50 }}>
          <span data-secure-kicker>{copy.kicker}</span>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 0, padding: 4, color: T.white, display: 'flex', cursor: 'pointer' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        <span style={{ width: 56, height: 56, borderRadius: '50%', background: T.white, color: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LockIcon size={22} />
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
          <span data-secure-title style={{ fontSize: 16, fontWeight: 700, letterSpacing: '.14em' }}>{copy.title}</span>
          <span style={{ fontSize: 10, letterSpacing: '.12em', color: SECURE.muted65 }}>{copy.sub}</span>
        </div>

        {modal.kind === 'setup' && step === 'enter' && (
          <>
            <span style={{ fontFamily: BODY_FONT, fontSize: 13, lineHeight: 1.45, color: SECURE.muted75, textAlign: 'center', textWrap: 'pretty' }}>{SETUP_NOTE}</span>
            <div role="radiogroup" aria-label="Lock type" style={{ display: 'flex', gap: 4, padding: 4, borderRadius: RADIUS.pill, background: SECURE.track }}>
              {['pin', 'pattern'].map((k) => {
                const on = type === k;
                return (
                  <button key={k} type="button" role="radio" aria-checked={on} onClick={() => pickType(k)} className="font-mono" style={{ height: 32, padding: '0 20px', borderRadius: RADIUS.pill, border: '2px solid transparent', fontSize: 10, fontWeight: 700, letterSpacing: '.12em', background: on ? fillFor('on') : 'transparent', color: T.white, cursor: 'pointer' }}>
                    {LOCK_TYPE_LABEL[k]}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {forgot && (
          <>
            <span style={{ fontFamily: BODY_FONT, fontSize: 13, lineHeight: 1.45, color: SECURE.muted75, textAlign: 'center', textWrap: 'pretty' }}>{FORGOT_NOTE}</span>
            <input
              type="password"
              value={pw}
              autoFocus
              onChange={(e) => { setPw(e.target.value); setError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') submitPw(); }}
              placeholder="ACCOUNT PASSWORD"
              aria-label="Account password"
              className="font-mono placeholder:text-white/40 focus:outline-none focus:ring-0"
              style={{ width: '100%', height: 44, boxSizing: 'border-box', borderRadius: RADIUS.pill, border: '2px solid rgba(255,255,255,.3)', background: 'transparent', color: T.white, padding: '0 18px', fontSize: 12, letterSpacing: '.1em' }}
            />
            <button type="button" onClick={submitPw} disabled={busy} className="font-mono hover:bg-black hover:text-white" style={{ width: '100%', height: 44, borderRadius: RADIUS.pill, border: `2px solid ${T.white}`, background: T.white, color: T.ink, fontSize: 11, fontWeight: 700, letterSpacing: '.14em', transition: 'background-color 200ms, color 200ms', cursor: 'pointer' }}>
              [ CONTINUE ]
            </button>
          </>
        )}

        {isPin && (
          <>
            <div data-pin-dots aria-label={`${input.length} of ${PIN_LENGTH} digits`} style={{ display: 'flex', gap: 16, padding: '4px 0' }}>
              {Array.from({ length: PIN_LENGTH }, (_, i) => {
                const filled = input.length > i;
                const err = !!error;
                return <span key={i} data-filled={filled ? '' : undefined} style={{ width: 16, height: 16, borderRadius: '50%', boxSizing: 'border-box', border: `2px solid ${err ? SECURE.error : T.white}`, background: err ? SECURE.error : filled ? T.white : 'transparent' }} />;
              })}
            </div>
            <div data-pin-pad style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 68px)', gap: '14px 22px', opacity: padOpacity }}>
              {PIN_KEYS.map((k, i) => (
                <button
                  key={i}
                  type="button"
                  // The digit lands on touch-down, not on the click a phone synthesises
                  // afterwards; the click is kept only for a keyboard (Enter / Space).
                  onPointerDown={(e) => { if (e.button === 0) { e.preventDefault(); pinPress(k); } }}
                  onClick={(e) => { if (e.detail === 0) pinPress(k); }}
                  disabled={!k || lockedOut}
                  aria-label={k === 'DEL' ? 'Delete' : k || undefined}
                  className="font-mono pr-pin-key"
                  style={{ width: 68, height: 68, borderRadius: '50%', fontSize: k === 'DEL' ? 11 : 22, fontWeight: 700, letterSpacing: '.06em', background: 'transparent', color: T.white, border: `2px solid ${k === 'DEL' || !k ? 'transparent' : SECURE.keyBorder}`, visibility: k ? 'visible' : 'hidden', cursor: 'pointer', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', userSelect: 'none' }}
                >
                  {k}
                </button>
              ))}
            </div>
          </>
        )}

        {isPattern && (
          <div
            ref={padRef}
            data-pattern-pad
            role="application"
            aria-label="Pattern pad"
            onPointerDown={patDown}
            onPointerMove={patMove}
            onPointerUp={patUp}
            onPointerCancel={patUp}
            style={{ position: 'relative', width: PAD_SIZE, height: PAD_SIZE, maxWidth: '100%', touchAction: 'none', userSelect: 'none', cursor: 'crosshair', opacity: padOpacity }}
          >
            {lines.map((l, i) => (
              <span key={i} style={{ position: 'absolute', left: l.x, top: l.y - 2, width: l.len, height: 4, borderRadius: 2, transformOrigin: '0 50%', transform: `rotate(${l.ang}deg)`, pointerEvents: 'none', background: patErr ? SECURE.error : RING }} />
            ))}
            {Array.from({ length: 9 }, (_, i) => {
              const on = seq.includes(i);
              const c = dotCentre(i);
              return (
                <span key={i} data-dot={i} data-on={on ? '' : undefined} style={{ position: 'absolute', left: c.x, top: c.y, width: 48, height: 48, margin: '-24px 0 0 -24px', borderRadius: '50%', pointerEvents: 'none', boxSizing: 'border-box', border: `2px solid ${on ? (patErr ? SECURE.error : T.white) : 'transparent'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border-color 120ms' }}>
                  <span style={{ width: on ? 16 : 10, height: on ? 16 : 10, borderRadius: '50%', background: patErr && on ? SECURE.error : T.white, transition: 'width 120ms, height 120ms' }} />
                </span>
              );
            })}
          </div>
        )}

        {modal.kind === 'unlock' && !forgot && biometric && (
          <button
            type="button"
            data-biometric-unlock
            onClick={askFace}
            disabled={busy || lockedOut}
            className="font-mono font-bold hover:bg-white hover:text-black"
            style={{ height: 36, padding: '0 16px', borderRadius: RADIUS.pill, border: `2px solid ${T.white}`, background: 'transparent', color: T.white, fontSize: 10, letterSpacing: '.12em', transition: 'background-color 200ms, color 200ms', cursor: 'pointer', opacity: lockedOut ? 0.3 : 1 }}
          >
            USE {biometric.label}
          </button>
        )}

        <span data-secure-error role="alert" style={{ minHeight: 14, fontSize: 10, fontWeight: 700, letterSpacing: '.1em', color: SECURE.error, textAlign: 'center' }}>{shownError}</span>

        <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
          {modal.kind === 'unlock' && !forgot && (
            <button type="button" onClick={() => { setStep('forgot'); setPat(null); setInput(''); setError(''); setPw(''); }} style={footLink}>FORGOT {LOCK_TYPE_LABEL[type]}?</button>
          )}
          {modal.kind === 'setup' && step === 'confirm' && (
            <button type="button" onClick={() => { setStep('enter'); setFirst(null); setInput(''); setPat(null); setError(''); }} style={footLink}>START OVER</button>
          )}
          <button type="button" onClick={onClose} style={{ ...footLink, color: SECURE.muted55, textDecoration: 'none' }}>CANCEL</button>
        </div>
      </div>
    </div>
  );
}
